/* Photo and artwork-file attachments on the contact form.
 *
 * Shopify's `form 'contact'` CANNOT carry a file — it posts to Shopify and
 * emails the fields, and there is no attachment field to post into. So the
 * picture never rides the form: it is uploaded to our own backend first (the
 * same /api/upload the designer uses, through the app proxy, which stores it
 * in R2 and enforces the size/format/quota rules), and only the resulting
 * LINK is written into a hidden text field that Shopify does email.
 *
 * Why this exists: the refund policy's whole mechanism is "send us a photo
 * and we reprint" — three policy pages now point customers at this form, and
 * before this they arrived with no way to show the damage.
 *
 * Failure is always soft. If an upload fails the customer still gets to send
 * the message; they are told the photo did not attach rather than being
 * blocked from reporting a problem.
 *
 * WHAT THE EMAIL SAYS, AND WHEN (owner 2026-09-09: "if a photos email shows
 * nothing there does that mean nothing was uploaded"). It used to post the
 * hidden field on EVERY message, so every plain enquiry arrived with an
 * empty "Photos:" line and nothing told a blank apart from a failed attempt.
 * Now:
 *   - no photo chosen            -> the field is DISABLED, so it is not
 *                                   posted and the email has no Photos line
 *   - photo(s) uploaded          -> the links, one per line
 *   - photo chosen, upload FAILED -> a note naming the file and the reason,
 *                                   so the owner knows to ask for it again
 * And the picker itself starts hidden in the markup and is revealed here:
 * if this script never loads, there is no picker to pick into, instead of
 * one that silently does nothing.
 */
(function () {
  'use strict';

  var MAX_FILES = 4;
  var MAX_BYTES = 25 * 1024 * 1024;           // matches image_probe.MAX_BYTES
  var OK_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

  // ARTWORK FILES (owner 2026-10-13, "we may need to allow pdf": a customer's
  // Snacks_In_A_Snap_..._Artwork_Specifications.pdf was refused). Same door as
  // the photos, /api/upload, which already reads a PDF (a modern .ai is a PDF
  // inside) and an SVG by their own bytes, never by the name or type the
  // browser reports. So this list only decides what is worth SENDING; the
  // backend still refuses anything that is not really that file, and a
  // refusal stays soft. Matched by extension because browsers report
  // .ai and .svg inconsistently (empty type, application/octet-stream, ...).
  var FILE_EXT = { pdf: 'PDF', ai: 'AI', svg: 'SVG' };
  var TYPE_ERROR = 'we accept JPG, PNG and WebP photos, and PDF, AI and SVG files';

  // THE REAL REASON (owner 2026-10-13, Lorry Gilbreath's second PNG came
  // through as "the upload did not go through" and nobody could tell why).
  // Every refusal now says what went wrong, in words the customer can act
  // on. All four are editable on the contact form section (theme editor);
  // these are the defaults if a setting is blank.
  var WORDS = {
    notImage: "this file isn't a real JPG, PNG or WebP picture (iPhone photos are often HEIC inside). Please send a screenshot of it, or save it as a JPG, and attach that",
    heic: 'iPhone HEIC photos do not open here. Please send a screenshot of it, or save it as a JPG, and attach that',
    tooBig: 'files must be under 25 MB',
    busy: 'our site was busy for a moment. Please attach it again in a minute'
  };
  // The probe's technical refusals (engine/image_probe.py) that all mean
  // "this is not really a picture we read". The other refusals the server
  // sends (too many pixels, a damaged file, a busy hour, a bad PDF) are
  // already written for the customer and are shown as they come.
  var NOT_IMAGE = /not a PNG, JPEG or WebP|truncated PNG|no JPEG frame|WebP|too small to be an image|reports no size/i;

  // What to tell the customer for one failed upload. `status` is the HTTP
  // code (0 when the request never got an answer), `detail` the server's
  // own sentence.
  function reasonFor(status, detail, words) {
    var w = words || WORDS;
    detail = String(detail || '').trim();
    if (status === 413) return w.tooBig;
    if (status === 415) return (!detail || NOT_IMAGE.test(detail)) ? w.notImage : detail;
    if (status === 429 && detail) return detail;
    if (!status || status >= 500) return w.busy;
    return detail || w.busy;
  }

  function isHeic(file) {
    return /^image\/hei[cf]/i.test(file.type || '') || /^(heic|heif)$/.test(extOf(file.name));
  }

  function extOf(name) {
    var m = /\.([A-Za-z0-9]+)$/.exec(String(name || ''));
    return m ? m[1].toLowerCase() : '';
  }

  // 'photo', 'PDF', 'AI', 'SVG', or '' when we do not take it.
  function kindOf(file) {
    if (OK_TYPES.indexOf(file.type) !== -1) return 'photo';
    return FILE_EXT[extOf(file.name)] || '';
  }

  // The lines the OWNER reads in the email for one stored upload.
  //  - a photo: its link, as it always was
  //  - a PDF / AI / SVG: the FILE link (?file=1 makes the backend hand over
  //    the file itself, as a download) and, for a PDF or AI, a page-1
  //    picture link, because the plain link is the picture the designer
  //    draws and is the quickest look at what they sent
  function linesFor(item) {
    if (item.kind === 'photo') return [item.url];
    var label = item.kind + ' file ' + String(item.name).replace(/[\r\n"]+/g, ' ');
    var out = [label + ': ' + item.url + '?file=1'];
    if (item.kind !== 'SVG') out.push(item.kind + ' page 1 picture: ' + item.url);
    return out;
  }

  function init(root) {
    var input = root.querySelector('[data-mg-upload-input]');
    var hidden = root.querySelector('[data-mg-upload-value]');
    var list = root.querySelector('[data-mg-upload-list]');
    var status = root.querySelector('[data-mg-upload-status]');
    if (!input || !hidden || !list || !status) return;

    var apiBase = root.getAttribute('data-api-base') || '/apps/design';
    var typeError = root.getAttribute('data-type-error') || TYPE_ERROR;
    var words = {
      notImage: root.getAttribute('data-not-image-error') || WORDS.notImage,
      heic: root.getAttribute('data-heic-error') || WORDS.heic,
      tooBig: root.getAttribute('data-too-big-error') || WORDS.tooBig,
      busy: root.getAttribute('data-busy-error') || WORDS.busy
    };
    var form = root.closest('form');
    var submit = form ? form.querySelector('[type="submit"]') : null;
    var uploaded = [];                         // {name, url, kind}
    var failed = [];                           // {name, why}
    var pending = 0;

    // The script is here, so the picker can do its job: show it.
    input.hidden = false;

    function say(msg, kind) {
      status.textContent = msg || '';
      status.className = 'mg-upload__status' + (kind ? ' mg-upload__status--' + kind : '');
    }

    function sync() {
      var lines = [];
      uploaded.forEach(function (u) { lines = lines.concat(linesFor(u)); });
      failed.forEach(function (f) {
        // The code is for the owner only (0 = no answer at all, 5xx = the
        // site was down or restarting, 4xx = the file was refused).
        lines.push('(Customer tried to attach ' + f.name + ' but it did not upload: ' + f.why +
          (f.code !== undefined ? ' [code ' + f.code + ']' : '') + '.)');
      });
      hidden.value = lines.join('\n');
      // Not posted at all while there is nothing to say — see the header.
      hidden.disabled = !hidden.value;
      // Never leave the customer unable to send. Only hold the button while
      // an upload is genuinely in flight.
      if (submit) {
        submit.disabled = pending > 0;
        submit.setAttribute('aria-busy', pending > 0 ? 'true' : 'false');
      }
    }

    function render() {
      list.textContent = '';
      uploaded.forEach(function (item, i) {
        var row = document.createElement('li');
        row.className = 'mg-upload__item';

        var name = document.createElement('span');
        name.className = 'mg-upload__name';
        name.textContent = item.name;

        var remove = document.createElement('button');
        remove.type = 'button';                // never submits the form
        remove.className = 'mg-upload__remove';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', 'Remove ' + item.name);
        remove.addEventListener('click', function () {
          uploaded.splice(i, 1);
          render();
          sync();
          say(uploaded.length ? '' : 'Removed.');
        });

        row.appendChild(name);
        row.appendChild(remove);
        list.appendChild(row);
      });
    }

    function reject(file, why, code) {
      // Remembered, and told to the owner in the email: a blank must never
      // be mistaken for "the customer did not try".
      failed.push({ name: file.name, why: why, code: code });
      sync();
      say('Could not attach ' + file.name + ': ' + why + '. You can still send your message.', 'error');
    }

    function upload(file) {
      if (uploaded.length + pending >= MAX_FILES) {
        return reject(file, 'you can attach up to ' + MAX_FILES + ' files');
      }
      if (isHeic(file)) {
        return reject(file, words.heic);
      }
      var kind = kindOf(file);
      if (!kind) {
        return reject(file, typeError);
      }
      if (file.size > MAX_BYTES) {
        return reject(file, words.tooBig);
      }

      pending += 1;
      sync();
      say('Attaching ' + file.name + '…');

      // ONE quiet retry, 4 seconds later, when the site gave no answer or a
      // 5xx: that is the site restarting after an update, which takes a
      // moment. A refusal (4xx) is never retried; it is told as it is.
      function send(retried) {
        return fetch(apiBase + '/api/upload?name=' + encodeURIComponent(file.name.slice(0, 80)), {
          method: 'POST',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file
        }).then(function (res) {
          if (res.ok) return res.json();
          if (res.status >= 500 && !retried) return later();
          return res.json().catch(function () { return {}; }).then(function (body) {
            var detail = body && typeof body.detail === 'string' ? body.detail : '';
            var err = new Error(detail);
            err.status = res.status;
            err.detail = detail;
            throw err;
          });
        }, function (netErr) {
          if (!retried) return later();
          throw netErr;
        });
      }
      function later() {
        return new Promise(function (ok) { setTimeout(ok, 4000); })
          .then(function () { return send(true); });
      }

      send(false)
        .then(function (data) {
          if (!data || !data.key) throw new Error('no key returned');
          // ABSOLUTE, and on the shop's own domain.
          //  - absolute because this value is emailed: a relative href has
          //    nothing to resolve against in a mail client, so it arrives
          //    dead. (The customer-account extension lost a day to exactly
          //    this.)
          //  - via the app proxy rather than the backend host, so the link
          //    keeps working if the backend moves and the Render URL is
          //    never exposed in an email.
          // The BACKEND decides what the file really is: a ".svg" that was a
          // photo inside comes back as a picture, so the lines follow its
          // answer, not the name.
          var stored = String(data.key);
          var storedKind = kind === 'photo' ? 'photo'
            : /\.pdf$/.test(stored) ? (kind === 'AI' ? 'AI' : 'PDF')
            : /\.vec\.json$/.test(stored) ? 'SVG'
            : 'photo';
          uploaded.push({
            name: file.name,
            kind: storedKind,
            url: window.location.origin + apiBase + '/api/uploads/' + stored
          });
          render();
          say(uploaded.length === 1 ? 'File attached.' : uploaded.length + ' files attached.', 'ok');
        })
        .catch(function (err) {
          var status = err && err.status ? err.status : 0;
          reject(file, reasonFor(status, err && err.detail, words), status);
        })
        .then(function () {
          pending -= 1;
          sync();
        });
    }

    input.addEventListener('change', function () {
      Array.prototype.slice.call(input.files || []).forEach(upload);
      input.value = '';                        // let the same file be re-picked
    });

    sync();
  }

  function boot() {
    document.querySelectorAll('[data-mg-upload]').forEach(init);
  }

  // For the test probe (backend/tests/js/contact_upload_probe.js).
  window.__mgContactUpload = { kindOf: kindOf, linesFor: linesFor,
    reasonFor: reasonFor, isHeic: isHeic, WORDS: WORDS };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
